import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from './Button';

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  z-index: 19998;
  font-family: ${theme.typography.fontFamily};
  pointer-events: ${props => props.allowClick ? 'auto' : 'none'};
`;

const Spotlight = styled.div`
  position: fixed;
  border: 3px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  pointer-events: none;
  z-index: 19999;
  transition: all 0.3s ease;
  box-shadow: 
    0 0 0 99999px rgba(0, 0, 0, 0.6),
    0 0 30px ${theme.colors.primary},
    0 0 60px rgba(114, 210, 255, 0.2),
    inset 0 0 20px rgba(114, 210, 255, 0.4);
`;

const ContentPanel = styled.div`
  position: fixed;
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.lg};
  padding: 0;
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  font-family: ${theme.typography.fontFamily};
  overflow: hidden;
  z-index: 20001;
  max-width: 550px;
  width: fit-content;
  min-width: 300px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
`;

const ProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 4px;
  background: ${theme.colors.primary};
  transition: width 0.3s ease;
  z-index: 1;
`;

const Header = styled.div`
  background: ${theme.colors.surface};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const Title = styled.div`
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.lg};
  color: ${theme.colors.text};
  letter-spacing: ${theme.typography.letterSpacing.normal};
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.xl};
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.sm};
  transition: ${theme.transitions.normal};
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
`;

const Content = styled.div`
  padding: ${theme.spacing.lg};
  overflow-y: auto;
  flex: 1;
  color: ${theme.colors.text};
  line-height: 1.6;
  font-size: ${theme.typography.fontSizes.base};
`;

const Footer = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.md};
  background: ${theme.colors.surface};
  flex-shrink: 0;
`;

const ProgressText = styled.div`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const TutorialOverlay = ({ 
  isVisible, 
  onClose, 
  currentStep, 
  totalSteps,
  step,
  onNext,
  onPrevious,
  onSkip,
  highlightElement,
  tooltipPosition
}) => {
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [contentPanelRect, setContentPanelRect] = useState(null);
  const contentPanelRef = useRef(null);

  useEffect(() => {
    if (!isVisible) {
      setSpotlightRect(null);
      setContentPanelRect(null);
      return;
    }

    const updatePositions = () => {
      let element = null;
      
      if (highlightElement) {
        if (typeof highlightElement === 'string') {
          // If it's already a CSS selector (starts with [ or . or #), use it directly
          if (highlightElement.startsWith('[') || highlightElement.startsWith('.') || highlightElement.startsWith('#')) {
            element = document.querySelector(highlightElement);
          } else {
            // Otherwise, treat it as a data attribute name
            element = document.querySelector(`[data-tutorial="${highlightElement}"]`);
          }
        } else {
          element = highlightElement;
        }
      }
      
      if (element) {
        const rect = element.getBoundingClientRect();
        setSpotlightRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });

        // Position content panel next to the highlighted element
        if (contentPanelRef.current) {
          const panelRect = contentPanelRef.current.getBoundingClientRect();
          const position = tooltipPosition || 'right';
          const spacing = 20;
          const padding = 20;
          
          let top, left;
          
          // Determine best position based on available space
          const spaceRight = window.innerWidth - rect.right;
          const spaceLeft = rect.left;
          const spaceBottom = window.innerHeight - rect.bottom;
          const spaceTop = rect.top;
          
          // Use preferred position if there's enough space, otherwise choose best available
          let finalPosition = position;
          if (position === 'right' && spaceRight < panelRect.width + spacing) {
            finalPosition = spaceLeft > spaceRight ? 'left' : 'right';
          } else if (position === 'left' && spaceLeft < panelRect.width + spacing) {
            finalPosition = spaceRight > spaceLeft ? 'right' : 'left';
          } else if (position === 'bottom' && spaceBottom < panelRect.height + spacing) {
            finalPosition = spaceTop > spaceBottom ? 'top' : 'bottom';
          } else if (position === 'top' && spaceTop < panelRect.height + spacing) {
            finalPosition = spaceBottom > spaceTop ? 'bottom' : 'top';
          }
          
          switch (finalPosition) {
            case 'right':
              top = rect.top + (rect.height / 2) - (panelRect.height / 2);
              left = rect.right + spacing;
              break;
            case 'left':
              top = rect.top + (rect.height / 2) - (panelRect.height / 2);
              left = rect.left - panelRect.width - spacing;
              break;
            case 'bottom':
              top = rect.bottom + spacing;
              left = rect.left + (rect.width / 2) - (panelRect.width / 2);
              break;
            case 'top':
              top = rect.top - panelRect.height - spacing;
              left = rect.left + (rect.width / 2) - (panelRect.width / 2);
              break;
            default:
              top = rect.bottom + spacing;
              left = rect.right + spacing;
          }

          // Keep panel in viewport
          if (left < padding) left = padding;
          if (left + panelRect.width > window.innerWidth - padding) {
            left = window.innerWidth - panelRect.width - padding;
          }
          if (top < padding) top = padding;
          if (top + panelRect.height > window.innerHeight - padding) {
            top = window.innerHeight - panelRect.height - padding;
          }

          setContentPanelRect({ top, left, position: finalPosition });
        }
      } else {
        // No element to highlight - center the panel
        setSpotlightRect(null);
        if (contentPanelRef.current) {
          const panelRect = contentPanelRef.current.getBoundingClientRect();
          setContentPanelRect({
            top: (window.innerHeight - panelRect.height) / 2,
            left: (window.innerWidth - panelRect.width) / 2,
            position: 'center'
          });
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(updatePositions, 100);
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);
    };
  }, [isVisible, highlightElement, tooltipPosition, step]);

  if (!isVisible) return null;

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      <Backdrop 
        onClick={step?.allowBackdropClick ? onClose : undefined}
        allowClick={step?.allowBackdropClick}
      />
      
      {spotlightRect && (
        <Spotlight
          style={{
            top: `${spotlightRect.top - 3}px`,
            left: `${spotlightRect.left - 3}px`,
            width: `${spotlightRect.width + 6}px`,
            height: `${spotlightRect.height + 6}px`,
          }}
        />
      )}

      <ContentPanel
        ref={contentPanelRef}
        onClick={(e) => e.stopPropagation()}
        style={contentPanelRect ? {
          top: `${contentPanelRect.top}px`,
          left: `${contentPanelRect.left}px`,
        } : {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <ProgressBar style={{ width: `${progress}%` }} />
        <Header>
          <Title>{step?.title || 'Tutorial'}</Title>
          <CloseButton onClick={onClose} title="Close tutorial">
            ✕
          </CloseButton>
        </Header>
        
        <Content>
          {step?.content && (
            <div>
              {step.content}
            </div>
          )}
        </Content>
        
        <Footer>
          <ProgressText>
            Step {currentStep + 1} of {totalSteps}
          </ProgressText>
          <ButtonGroup>
            {onPrevious && (
              <Button
                onClick={onPrevious}
                disabled={currentStep === 0}
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  fontSize: theme.typography.fontSizes.sm,
                }}
              >
                Previous
              </Button>
            )}
            <Button
              onClick={onNext}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSizes.sm,
              }}
            >
              {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            </Button>
          </ButtonGroup>
        </Footer>
      </ContentPanel>
    </>
  );
};

export default TutorialOverlay;
